import React, { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileBarChart, FileText, FileSpreadsheet, User, BookOpen, CalendarCheck, ScanFace } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PdfButtons } from "@/components/DiaryPrint";

const apiBase = `${process.env.REACT_APP_BACKEND_URL}/api`;
const openReport = (path, params) => {
  const qs = new URLSearchParams(params).toString();
  window.open(`${apiBase}${path}?${qs}`, "_blank", "noopener");
};

export default function Relatorios() {
  const { user, scopeParams, selectedSchoolId } = useAuth();
  const [years, setYears] = useState([]);
  const [yearId, setYearId] = useState("");
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    api.get("/academic-years", { params: scopeParams }).then((r) => {
      setYears(r.data);
      if (r.data[0]) setYearId((y) => y || r.data[0].id);
    });
    api.get("/classes", { params: scopeParams }).then((r) => setClasses(r.data)).catch(() => setClasses([]));
    api.get("/students", { params: scopeParams }).then((r) => setStudents(r.data)).catch(() => setStudents([]));
  }, [scopeParams, selectedSchoolId]);

  return (
    <div>
      <PageHeader title="Central de Relatórios" subtitle="Ficha do estudante, Boletim e Mapa de Frequência — PDF para impressão e exportação XLSX">
        <Select value={yearId} onValueChange={setYearId}>
          <SelectTrigger className="w-40" data-testid="reports-year-select"><SelectValue placeholder="Ano letivo" /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.year}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <StudentFileCard students={students} classes={classes} />
        <ReportCardCard yearId={yearId} classes={classes} students={students} />
        <AttendanceMapCard yearId={yearId} classes={classes} />
        <AttendanceDayCard classes={classes} />
        {["SUPERADMIN", "DIRECAO", "COORDENACAO"].includes(user?.role) && <DiaryBulkCard classes={classes} scopeParams={scopeParams} />}
      </div>
    </div>
  );
}

function ReportShell({ icon: Icon, title, desc, children }) {
  return (
    <Card className="flex flex-col border-slate-200 p-5" data-testid={`report-card-${title}`}>
      <div className="mb-3 flex items-center gap-3">
        <div className="grid h-11 w-11 place-items-center rounded-xl bg-blue-50 text-blue-700"><Icon className="h-6 w-6" /></div>
        <div>
          <div className="font-display text-lg font-bold text-slate-900">{title}</div>
          <div className="text-xs text-slate-400">{desc}</div>
        </div>
      </div>
      <div className="mt-auto space-y-3">{children}</div>
    </Card>
  );
}

function ClassSelect({ value, onChange, classes, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder="Selecione a turma" /></SelectTrigger>
      <SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.grade ? ` · ${c.grade}` : ""}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function StudentFileCard({ students, classes }) {
  const [classId, setClassId] = useState("");
  const [studentId, setStudentId] = useState("");
  const filtered = useMemo(() => students.filter((s) => !classId || s.class_id === classId), [students, classId]);

  return (
    <ReportShell icon={User} title="Ficha do Estudante" desc="Cadastro, responsáveis, desempenho e frequência">
      <ClassSelect value={classId} onChange={(v) => { setClassId(v); setStudentId(""); }} classes={classes} testid="ficha-class-select" />
      <Select value={studentId} onValueChange={setStudentId} disabled={!filtered.length}>
        <SelectTrigger data-testid="ficha-student-select"><SelectValue placeholder="Selecione o estudante" /></SelectTrigger>
        <SelectContent>{filtered.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
      </Select>
      <Button className="w-full bg-blue-700 hover:bg-blue-800" disabled={!studentId} data-testid="ficha-pdf-button"
        onClick={() => openReport(`/reports/student-file/${studentId}`, {})}>
        <FileText className="mr-1.5 h-4 w-4" /> Gerar Ficha (PDF)
      </Button>
    </ReportShell>
  );
}

function ReportCardCard({ yearId, classes, students }) {
  const [classId, setClassId] = useState("");
  const [studentId, setStudentId] = useState("all");
  const filtered = useMemo(() => students.filter((s) => !classId || s.class_id === classId), [students, classId]);
  const base = () => ({ academic_year_id: yearId, class_id: classId, ...(studentId !== "all" ? { student_id: studentId } : {}) });

  return (
    <ReportShell icon={BookOpen} title="Boletim" desc="Notas por trimestre, média e situação">
      <ClassSelect value={classId} onChange={(v) => { setClassId(v); setStudentId("all"); }} classes={classes} testid="boletim-class-select" />
      <Select value={studentId} onValueChange={setStudentId} disabled={!classId}>
        <SelectTrigger data-testid="boletim-student-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Turma inteira</SelectItem>
          {filtered.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex gap-2">
        <Button className="flex-1 bg-blue-700 hover:bg-blue-800" disabled={!classId || !yearId} data-testid="boletim-pdf-button"
          onClick={() => openReport("/reports/report-card", { ...base(), format: "pdf" })}>
          <FileText className="mr-1.5 h-4 w-4" /> PDF
        </Button>
        <Button variant="outline" className="flex-1" disabled={!classId || !yearId} data-testid="boletim-xlsx-button"
          onClick={() => openReport("/reports/report-card", { academic_year_id: yearId, class_id: classId, format: "xlsx" })}>
          <FileSpreadsheet className="mr-1.5 h-4 w-4" /> XLSX
        </Button>
      </div>
    </ReportShell>
  );
}

function AttendanceDayCard({ classes }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [classId, setClassId] = useState("all");
  const base = () => ({ date, ...(classId !== "all" ? { class_id: classId } : {}) });
  return (
    <ReportShell icon={ScanFace} title="Presença Diária" desc="Chegadas, saídas antecipadas e faltas do dia">
      <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="presday-date" />
      <Select value={classId} onValueChange={setClassId}>
        <SelectTrigger data-testid="presday-class-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">Todas as turmas</SelectItem>
          {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.grade ? ` · ${c.grade}` : ""}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="flex gap-2">
        <Button className="flex-1 bg-blue-700 hover:bg-blue-800" disabled={!date} data-testid="presday-pdf-button"
          onClick={() => openReport("/reports/attendance-day", { ...base(), format: "pdf" })}>
          <FileText className="mr-1.5 h-4 w-4" /> PDF
        </Button>
        <Button variant="outline" className="flex-1" disabled={!date} data-testid="presday-xlsx-button"
          onClick={() => openReport("/reports/attendance-day", { ...base(), format: "xlsx" })}>
          <FileSpreadsheet className="mr-1.5 h-4 w-4" /> XLSX
        </Button>
      </div>
    </ReportShell>
  );
}

function DiaryBulkCard({ classes, scopeParams }) {
  const [classId, setClassId] = useState("all");
  const [subjectId, setSubjectId] = useState("all");
  const [term, setTerm] = useState("1");
  const [subjects, setSubjects] = useState([]);
  useEffect(() => { api.get("/subjects", { params: scopeParams }).then((r) => setSubjects(r.data)).catch(() => setSubjects([])); }, [scopeParams]);
  const cls = classes.find((c) => c.id === classId);
  const subs = cls ? subjects.filter((s) => (cls.subject_ids || []).includes(s.id)) : subjects;
  const params = { term_order: Number(term), ...(scopeParams || {}), ...(cls ? { class_id: classId } : {}), ...(subjectId !== "all" ? { subject_id: subjectId } : {}) };
  return (
    <ReportShell icon={BookOpen} title="Diários de Classe" desc="Uma turma/disciplina ou em massa (todas as turmas) — PDF por trimestre">
      <Select value={classId} onValueChange={(v) => { setClassId(v); setSubjectId("all"); }}>
        <SelectTrigger data-testid="diary-bulk-class"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">Todas as turmas</SelectItem>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.grade ? ` · ${c.grade}` : ""}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={subjectId} onValueChange={setSubjectId}>
        <SelectTrigger data-testid="diary-bulk-subject"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">Todas as disciplinas</SelectItem>{subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={term} onValueChange={setTerm}>
        <SelectTrigger data-testid="diary-bulk-term"><SelectValue /></SelectTrigger>
        <SelectContent>{["1", "2", "3"].map((t) => <SelectItem key={t} value={t}>{t}º Trimestre</SelectItem>)}</SelectContent>
      </Select>
      <PdfButtons path="/diary/pdf/bulk" params={params} filename={`diarios_${cls ? cls.name : "todas"}_${term}tri.pdf`} testid="diary-bulk" />
    </ReportShell>
  );
}

function AttendanceMapCard({ yearId, classes }) {
  const [classId, setClassId] = useState("");
  return (
    <ReportShell icon={CalendarCheck} title="Mapa de Frequência" desc="Percentual por disciplina e situação (corte 75%)">
      <ClassSelect value={classId} onChange={setClassId} classes={classes} testid="freq-class-select" />
      <div className="flex gap-2">
        <Button className="flex-1 bg-blue-700 hover:bg-blue-800" disabled={!classId || !yearId} data-testid="freq-pdf-button"
          onClick={() => openReport("/reports/attendance-map", { academic_year_id: yearId, class_id: classId, format: "pdf" })}>
          <FileText className="mr-1.5 h-4 w-4" /> PDF
        </Button>
        <Button variant="outline" className="flex-1" disabled={!classId || !yearId} data-testid="freq-xlsx-button"
          onClick={() => openReport("/reports/attendance-map", { academic_year_id: yearId, class_id: classId, format: "xlsx" })}>
          <FileSpreadsheet className="mr-1.5 h-4 w-4" /> XLSX
        </Button>
      </div>
    </ReportShell>
  );
}
